import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useStore } from '../store/useStore';
import { db } from '../db/db';
import Invoice from './print/Invoice';
import LedgerReport from './print/LedgerReport';
import { tauriSafePrint } from '../utils/tauriPrint.js';
import StockMovementReport from './print/StockMovement';
import DayBookPrint from './print/DayBookPrint';
import TrialBalancePrint from './print/TrialBalancePrint';
import BalanceSheetPrint from './print/BalanceSheetPrint';
import ProfitLossPrint from './print/ProfitLossPrint';
import StockSummaryPrint from './print/StockSummaryPrint';
import RegisterSummaryPrint from './print/RegisterSummaryPrint';
import VoucherRegisterPrint from './print/VoucherRegisterPrint';
import QuotationPrint from './print/QuotationPrint';
import VoucherPrint from './print/VoucherPrint';
import PharmacyInvoice from './print/PharmacyInvoice';
import { AccountingEngine } from '../services/accountingEngine';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';

import { exportToExcel, exportToPdf } from '../lib/exportHelpers';

// ─── Popup Print Helper ───────────────────────────────────────────────────────
// Ye function content div ka HTML leke ek clean popup window mein print karta hai.
// Is se grey background, toolbar, scroll area sab skip hote hain.
function popupPrint(contentEl: HTMLElement | null) {
  if (!contentEl) {
    window.print();
    return;
  }

  const html = contentEl.innerHTML;

  // Current page ke saare <link rel="stylesheet"> aur <style> tags collect karo
  const styleLinks = Array.from(document.querySelectorAll('link[rel="stylesheet"]'))
    .map((el) => el.outerHTML)
    .join('\n');
  const styleTags = Array.from(document.querySelectorAll('style'))
    .map((el) => el.outerHTML)
    .join('\n');

  const fullHtml = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  ${styleLinks}
  ${styleTags}
  <style>
    @page { size: A4; margin: 8mm; }
    * { box-sizing: border-box; }
    html, body {
      margin: 0;
      padding: 0;
      background: white !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      min-width: 0 !important;
      min-height: 0 !important;
      overflow: visible !important;
    }
    body { font-family: Arial, sans-serif; color: #000; }
    .invoice-wrapper, .print-report, .ledger-wrapper, .report-wrapper {
      box-shadow: none !important;
      margin: 0 auto !important;
      border: none !important;
      min-height: 0 !important;
    }
    .no-print { display: none !important; }
    thead { display: table-header-group; }
    tfoot { display: table-footer-group; }
    tr { page-break-inside: avoid; }
  </style>
</head>
<body>
  ${html}
  <!-- print() intentionally removed: tauriSafePrint handles print()+cleanup.
       Having both caused a double print dialog. -->
</body>
</html>`;

  tauriSafePrint(fullHtml);
}
// ─────────────────────────────────────────────────────────────────────────────

export const PrintManager = ({ activeMenuLevel, selectedEntityId }: { activeMenuLevel: string, selectedEntityId: number | null }) => {
  const printRequested = useStore(s => s.printRequested);
  const setPrintRequested = useStore(s => s.setPrintRequested);
  const exportRequested = useStore(s => s.exportRequested);
  const setExportRequested = useStore(s => s.setExportRequested);
  const activeCompany = useStore(s => s.activeCompany);
  const fromDate = useStore(s => s.fromDate);
  const toDate = useStore(s => s.toDate);
  
  const [printData, setPrintData] = useState<any>(null);
  const [reportType, setReportType] = useState<string | null>(null);
  const [zoom, setZoom] = useState<number>(100);
  const contentRef = useRef<HTMLDivElement>(null);
  const printContentRef = useRef<HTMLDivElement>(null); // popup print ke liye
  const isGatheringRef = React.useRef(false); // Prevent double-fire (React StrictMode / rapid clicks)

  // Auto-fit zoom on data load
  useEffect(() => {
    if (printData && contentRef.current) {
      const timer = setTimeout(() => {
        autoFitZoom();
      }, 120);
      return () => clearTimeout(timer);
    }
  }, [printData]);

  // ESC key se preview close karo, Ctrl+P se directly print karo
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && printData) {
        e.preventDefault();
        e.stopPropagation();
        setPrintRequested(false);
        setPrintData(null);
        setReportType(null);
        setZoom(100);
        return;
      }
      // Ctrl+P: agar preview open hai toh direct print karo, naya gather mat karo
      if (e.ctrlKey && e.key?.toLowerCase() === 'p' && printData) {
        e.preventDefault();
        e.stopPropagation();
        popupPrint(printContentRef.current);
        return;
      }
      // W: WhatsApp share shortcut in print preview
      if ((e.key === 'w' || e.key === 'W') && printData && !e.ctrlKey && !e.altKey) {
        e.preventDefault(); e.stopPropagation();
        const d = printData as any;
        const text = d?.meta
          ? `*${d.company?.name || ''}*\n${d.meta.voucherType || ''} #${d.meta.referenceNumber || d.meta.voucherNumber || ''}\nDate: ${d.meta.date || ''}\nParty: ${d.meta.party || ''}`
          : `Document from ${d?.company?.name || 'your accounting software'}`;
        if (navigator.share) { navigator.share({ title: 'Invoice', text }).catch(() => window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank')); }
        else { window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank'); }
        return;
      }
    };
    // capture: true — useAppNavigation ke bubble-phase handler se pehle fire hoga
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [printData]);

  const autoFitZoom = useCallback(() => {
    if (!contentRef.current) return;
    const containerW = contentRef.current.clientWidth - 48; // 24px padding each side
    const pageW = 794; // ~210mm at 96dpi
    const fitZoom = Math.floor((containerW / pageW) * 100);
    setZoom(Math.min(100, Math.max(50, fitZoom)));
  }, []);

  const changeZoom = (delta: number) => {
    setZoom(prev => Math.min(150, Math.max(40, prev + delta)));
  };

  useEffect(() => {
    if ((printRequested || exportRequested) && !isGatheringRef.current) {
      isGatheringRef.current = true;
      gatherData().catch((err) => {
        console.error('PrintManager: gatherData error', err);
      }).finally(() => {
        isGatheringRef.current = false;
      });
    }
  }, [printRequested, exportRequested]);

  const gatherData = async () => {
    if (!activeCompany) {
      setPrintRequested(false);
      return;
    }

    let data: any = null;
    let type: string | null = null;

    try {
      if (activeMenuLevel === 'alter_voucher_form' && selectedEntityId) {
        const voucher = await db.vouchers.get(selectedEntityId);
        if (voucher) {
          const isInvoiceType = ['Sales', 'Purchase', 'Credit Note', 'Debit Note'].includes(voucher.type);
          const isPharmacy = (activeCompany as any).businessNature === 'pharmacy';
          type = isInvoiceType ? (isPharmacy ? 'pharmacy_invoice' : 'invoice') : 'voucher';
          const entries = await db.voucherEntries.where('voucherId').equals(selectedEntityId).toArray();
          const inventory = await db.inventoryEntries.where('voucherId').equals(selectedEntityId).toArray();
          
          const drEntry = entries.find(e => e.type === 'Dr');
          const crEntry = entries.find(e => e.type === 'Cr');
          
          const partyLedger = voucher.type === 'Sales' || voucher.type === 'Receipt' || voucher.type === 'Contra' 
            ? (drEntry?.ledgerId ? await db.ledgers.get(drEntry.ledgerId) : null)
            : (crEntry?.ledgerId ? await db.ledgers.get(crEntry.ledgerId) : null);

          const items = await Promise.all(inventory.map(async (inv, idx) => {
            const stockItem = inv?.stockItemId ? await db.stockItems.get(inv.stockItemId) : null;
            const unit = stockItem?.unitId ? await db.units.get(stockItem.unitId) : null;
            return {
              slNo: idx + 1,
              description: stockItem?.name || 'Unknown Item',
              quantity: inv.quantity,
              quantityDisplay: `${inv.quantity} ${unit?.symbol || ''}`.trim(),
              rate: inv.rate,
              per: unit?.symbol || '',
              amount: inv.amount,
              gstRate: stockItem?.gstRate || 0,
            };
          }));

          const ledgers = await db.ledgers.where('companyId').equals(activeCompany.id!).toArray();
          const displayItems = items.length > 0 ? items.map(it => ({
            ...it,
            quantity: it.quantityDisplay,
          })) : entries.map((e, idx) => ({
            slNo: idx + 1,
            description: `Account: ${ledgers.find(l => l.id === e.ledgerId)?.name || 'Unknown'}`,
            quantity: '',
            rate: e.amount,
            per: '',
            amount: e.amount,
            gstRate: 0,
          }));

          // Fetch party ledger's GSTIN if available
          const partyGstin = (partyLedger as any)?.gstin || '';
          const partyAddress = (partyLedger as any)?.address
            ? [(partyLedger as any).address]
            : [];

          // GST Calculation
          // Check if inter-state (party state != company state) → IGST, else CGST+SGST
          const partyState = (partyLedger as any)?.state || '';
          const companyState = activeCompany.state || '';
          const isInterState = partyState && companyState && partyState.trim().toLowerCase() !== companyState.trim().toLowerCase();

          // Calculate taxable amount from inventory items
          const taxableAmount = items.reduce((sum, it) => sum + (it.amount || 0), 0);

          // Build GST breakup per rate slab
          const gstSlabMap: Record<number, number> = {};
          for (const it of items) {
            const rate = it.gstRate || 0;
            if (rate > 0) {
              gstSlabMap[rate] = (gstSlabMap[rate] || 0) + (it.amount || 0);
            }
          }

          // Also check voucherEntries for GST ledger lines (CGST/SGST/IGST ledgers in 'Duties & Taxes')
          const allGroups = await db.groups.where('companyId').equals(activeCompany.id!).toArray();
          const groupMap = new Map(allGroups.map(g => [g.id, g.name]));
          const gstEntryRows: { name: string; amount: number }[] = [];
          for (const e of entries) {
            const led = ledgers.find(l => l.id === e.ledgerId);
            if (!led) continue;
            const grpName = led.groupId ? groupMap.get(led.groupId) : '';
            if (grpName === 'Duties & Taxes') {
              gstEntryRows.push({ name: led.name, amount: e.amount });
            }
          }

          // Build gstRows for invoice display
          let gstRows: { label: string; rate: number; amount: number }[] = [];
          if (gstEntryRows.length > 0) {
            // Use actual ledger entries (most accurate)
            for (const row of gstEntryRows) {
              gstRows.push({ label: row.name, rate: 0, amount: row.amount });
            }
          } else if (Object.keys(gstSlabMap).length > 0) {
            // Calculate from item gstRate
            for (const [rateStr, base] of Object.entries(gstSlabMap)) {
              const rate = Number(rateStr);
              if (isInterState) {
                const igstAmt = Math.round(base * rate) / 100;
                gstRows.push({ label: `IGST @ ${rate}%`, rate, amount: igstAmt });
              } else {
                const halfRate = rate / 2;
                const halfAmt = Math.round(base * halfRate) / 100;
                gstRows.push({ label: `CGST @ ${halfRate}%`, rate: halfRate, amount: halfAmt });
                gstRows.push({ label: `SGST @ ${halfRate}%`, rate: halfRate, amount: halfAmt });
              }
            }
          }

          const totalGst = gstRows.reduce((s, r) => s + r.amount, 0);
          const totalDrAmount = entries.filter(e => e.type === 'Dr').reduce((acc, curr) => acc + curr.amount, 0);
          const grandTotal = totalDrAmount; // voucher total already includes tax

          // Amount in Words helper
          const numberToWords = (n: number): string => {
            const ones = ['','One','Two','Three','Four','Five','Six','Seven','Eight','Nine',
              'Ten','Eleven','Twelve','Thirteen','Fourteen','Fifteen','Sixteen','Seventeen','Eighteen','Nineteen'];
            const tens = ['','','Twenty','Thirty','Forty','Fifty','Sixty','Seventy','Eighty','Ninety'];
            const toWords = (num: number): string => {
              if (num === 0) return '';
              if (num < 20) return ones[num] + ' ';
              if (num < 100) return tens[Math.floor(num/10)] + (num%10 ? ' ' + ones[num%10] : '') + ' ';
              if (num < 1000) return ones[Math.floor(num/100)] + ' Hundred ' + toWords(num%100);
              if (num < 100000) return toWords(Math.floor(num/1000)) + 'Thousand ' + toWords(num%1000);
              if (num < 10000000) return toWords(Math.floor(num/100000)) + 'Lakh ' + toWords(num%100000);
              return toWords(Math.floor(num/10000000)) + 'Crore ' + toWords(num%10000000);
            };
            const intPart = Math.floor(Math.abs(n));
            const decPart = Math.round((Math.abs(n) - intPart) * 100);
            let result = 'Rupees ' + (intPart === 0 ? 'Zero' : toWords(intPart).trim());
            if (decPart > 0) result += ' and ' + toWords(decPart).trim() + ' Paise';
            result += ' Only';
            return result;
          };

          if (type === 'pharmacy_invoice') {
            const pharmaItems = await Promise.all(inventory.map(async (inv, idx) => {
              const stockItem = inv?.stockItemId ? await db.stockItems.get(inv.stockItemId) : null;
              const unit = stockItem?.unitId ? await db.units.get(stockItem.unitId) : null;
              return {
                name: stockItem?.name || 'Item',
                pack: (stockItem as any)?.pack || unit?.symbol || '',
                hsn: stockItem?.hsn || '3004',
                batch: (inv as any)?.batchNumber || (stockItem as any)?.batchNumber || '',
                expiry: (inv as any)?.expiryDate || (stockItem as any)?.expiryDate || '',
                qty: inv.quantity,
                mrp: (stockItem as any)?.mrp || inv.rate,
                rate: inv.rate,
                discount: 0,
                amount: inv.amount || inv.quantity * inv.rate,
              };
            }));

            data = {
              company: {
                name: activeCompany.name,
                address: (activeCompany as any).address || '',
                gstin: (activeCompany as any).gstin || '',
                phone: (activeCompany as any).mobile || '',
                email: (activeCompany as any).email || '',
              },
              meta: { invoiceNo: voucher.voucherNumber, date: voucher.date },
              patient: { name: '', address: '' },
              doctor: { name: '', regNo: '' },
              items: pharmaItems.length > 0 ? pharmaItems : entries.map((e, idx) => {
                const led = ledgers.find(l => l.id === e.ledgerId);
                return { name: led?.name || '', pack: '', hsn: '', batch: '', expiry: '', qty: 1, mrp: e.amount, rate: e.amount, discount: 0, amount: e.amount };
              }),
              totals: {
                discount: 0,
                amount: pharmaItems.reduce((s, it) => s + (it.amount || 0), 0) || entries.reduce((s, e) => s + e.amount, 0) / 2,
                gstRows: [],
              },
            };
          } else if (type === 'voucher') {
            const ledgerMapV = new Map(ledgers.map(l => [l.id, l]));
            data = {
              company: {
                name: activeCompany.name,
                address: (activeCompany as any).address || '',
                gstin: (activeCompany as any).gstin || '',
                stateName: activeCompany.state || '',
                phone: (activeCompany as any).mobile || '',
              },
              meta: {
                invoiceNo: voucher.voucherNumber,
                voucherNo: voucher.voucherNumber,
                date: voucher.date,
                voucherType: voucher.type,
                narration: voucher.narration || '',
              },
              entries: entries.map(e => ({
                ledger: ledgerMapV.get(e.ledgerId)?.name || 'Unknown',
                debit: e.type === 'Dr' ? String(e.amount) : '',
                credit: e.type === 'Cr' ? String(e.amount) : '',
              })),
            };
          } else {

          data = {
            company: {
              name: activeCompany.name,
              address: activeCompany.address ? [activeCompany.address] : [],
              gstin: activeCompany.gstin || '',
              stateName: activeCompany.state || '',
              stateCode: '',
              email: (activeCompany as any).email || '',
            },
            meta: {
              invoiceNo: voucher.voucherNumber,
              date: voucher.date,
              deliveryNote: '-',
              termsOfPayment: '',
              voucherType: voucher.type,
              documentTitle: (() => {
                const t = (voucher.type || '').toLowerCase();
                if (t === 'sales') return 'TAX INVOICE';
                if (t === 'purchase') return 'PURCHASE INVOICE';
                if (t === 'credit note') return 'CREDIT NOTE';
                if (t === 'debit note') return 'DEBIT NOTE';
                if (t === 'delivery note') return 'DELIVERY CHALLAN';
                if (t === 'proforma') return 'PROFORMA INVOICE';
                return (voucher.type || 'TAX INVOICE').toUpperCase();
              })()
            },
            consignee: {
              name: partyLedger?.name || 'Cash',
              address: partyAddress,
              gstin: partyGstin,
              stateName: partyState,
              stateCode: ''
            },
            buyer: {
              name: partyLedger?.name || 'Cash',
              address: partyAddress,
              gstin: partyGstin,
              stateName: partyState,
              stateCode: ''
            },
            items: displayItems,
            totals: {
              quantity: items.length > 0
                ? items.reduce((acc, curr) => acc + (Number(curr.quantity) || 0), 0).toString()
                : '',
              taxableAmount,
              gstRows,
              totalGst,
              amount: grandTotal,
              amountInWords: numberToWords(grandTotal)
            },
            bank: {
              name: (activeCompany as any).bankName || '',
              acNo: (activeCompany as any).bankAcct || '',
              branchIfsc: (activeCompany as any).bankBranch && (activeCompany as any).bankIfsc
                ? `${(activeCompany as any).bankBranch} / ${(activeCompany as any).bankIfsc}`
                : (activeCompany as any).bankIfsc || (activeCompany as any).bankBranch || '',
            },
            declaration: 'We declare that this invoice shows the actual price of the goods described and that all particulars are true and correct.'
          };
          } // close else (invoice branch)
        }
      } else if ((activeMenuLevel === 'alter_quotation_form' || activeMenuLevel === 'creation_quotation') && selectedEntityId) {
        // ── Quotation Print (new manual format) ─────────────────────────────
        type = 'quotation';
        const quot = await (db as any).quotations.get(Number(selectedEntityId));
        if (quot) {
          data = { ...quot };
        }
      } else if (activeMenuLevel === 'daybook') {
        type = 'daybook';
        const vouchers = await db.vouchers
          .where('companyId').equals(activeCompany.id!)
          .filter(v => v.date >= fromDate && v.date <= toDate)
          .toArray();

        // Sort by date — same as DayBookView
        vouchers.sort((a, b) => a.date.localeCompare(b.date));

        const CHARGE_GROUPS = new Set(['Duties & Taxes', 'Direct Expenses', 'Indirect Expenses', 'Direct Income', 'Indirect Income']);
        const allGroups = await db.groups.where('companyId').equals(activeCompany.id!).toArray();
        const groupMap = new Map(allGroups.map(g => [g.id, g.name]));

        const voucherList = [];
        let totalDr = 0;
        let totalCr = 0;

        for (const v of vouchers) {
          const entries = await db.voucherEntries.where('voucherId').equals(v.id!).toArray();

          // Batch-load ledgers for this voucher
          const ledgerIds = [...new Set(entries.map(e => e.ledgerId))];
          const entryLedgers = await Promise.all(ledgerIds.map(id => db.ledgers.get(id)));
          const ledgerMap = new Map(entryLedgers.filter(Boolean).map(l => [l!.id, l!]));

          const isCharge = (e: any) => {
            const led = ledgerMap.get(e.ledgerId);
            if (!led) return false;
            const gName = (led as any).groupId ? groupMap.get((led as any).groupId) : '';
            return gName ? CHARGE_GROUPS.has(gName) : false;
          };

          // Lookup voucherType record for stockDirection (supports custom types)
          const voucherTypeRecord = await db.voucherTypes
            .where({ companyId: activeCompany.id!, name: v.type })
            .first();

          let partyDrCr: 'Dr' | 'Cr' = 'Dr';
          if (voucherTypeRecord?.stockDirection === 'Out') partyDrCr = 'Dr';
          else if (voucherTypeRecord?.stockDirection === 'In') partyDrCr = 'Cr';
          else if (['Purchase', 'Debit Note', 'Payment'].includes(v.type)) partyDrCr = 'Cr';

          const sideEntries = entries.filter(e => e.type === partyDrCr);
          const partyEntry =
            sideEntries.find(e => !isCharge(e)) ||
            sideEntries[0] ||
            entries[0];

          const partyLedger = partyEntry ? ledgerMap.get(partyEntry.ledgerId) : null;
          const particulars = partyLedger?.name || 'Unknown';

          const dr = entries.filter(e => e.type === 'Dr').reduce((sum, e) => sum + e.amount, 0);
          const cr = entries.filter(e => e.type === 'Cr').reduce((sum, e) => sum + e.amount, 0);
          totalDr += dr;
          totalCr += cr;

          voucherList.push({
            date: v.date,
            particulars,
            vchType: v.type,
            vchNo: v.voucherNumber,
            refNo: v.referenceNumber || '',
            narration: v.narration || '',
            debit: dr.toFixed(2),
            credit: cr.toFixed(2)
          });
        }

        data = {
          company: { name: activeCompany.name, address: activeCompany.address || '' },
          reportMeta: { fromDate, toDate },
          vouchers: voucherList,
          totals: { debit: totalDr.toFixed(2), credit: totalCr.toFixed(2) }
        };
      } else if (activeMenuLevel === 'trialbalance') {
        type = 'trialbalance';
        const tb = await AccountingEngine.generateTrialBalance(activeCompany.id!, fromDate, toDate);
        data = {
          company: { name: activeCompany.name, address: activeCompany.address || '' },
          reportMeta: { fromDate, toDate },
          items: tb.items,
          totalDebit: tb.totalDebit.toFixed(2),
          totalCredit: tb.totalCredit.toFixed(2)
        };
      } else if (activeMenuLevel === 'balancesheet') {
        type = 'balancesheet';
        const bs = await AccountingEngine.generateBalanceSheet(activeCompany.id!, fromDate, toDate);
        data = {
          company: { name: activeCompany.name, address: activeCompany.address || '' },
          reportMeta: { toDate },
          assets: bs.assets,
          liabilities: bs.liabilities,
          totalAssets: bs.totalAssets.toFixed(2),
          totalLiabilities: bs.totalLiabilities.toFixed(2)
        };
      } else if (activeMenuLevel === 'profitloss') {
        type = 'profitloss';
        const pl = await AccountingEngine.generateProfitLoss(activeCompany.id!, fromDate, toDate);
        data = {
          company: { name: activeCompany.name, address: activeCompany.address || '' },
          reportMeta: { fromDate, toDate },
          expenses: pl.expenses,
          incomes: pl.incomes,
          totalExpenses: pl.totalExpenses.toFixed(2),
          totalIncomes: pl.totalIncomes.toFixed(2)
        };
      } else if (activeMenuLevel === 'ledgervouchers' && selectedEntityId) {
        type = 'ledger';
        // Use engine method — consistent party detection (stockDirection-aware), sorted, pre-period opening balance
        const report = await AccountingEngine.generateLedgerReport(selectedEntityId, fromDate, toDate);
        if (report) {
          const { ledger, vouchers: reportVouchers, openingBalance, openingType, closingBalance, closingType } = report;

          const transactions: any[] = [];

          // Opening balance row
          transactions.push({
            date: fromDate,
            particulars: { main: 'Opening Balance' },
            isOpening: true,
            balance: `${openingBalance} ${openingType}`
          });

          for (const v of reportVouchers) {
            transactions.push({
              date: v.date,
              particulars: { main: v.particulars },
              vchType: v.vchType,
              vchNo: v.vchNo,
              refNo: v.refNo || '',
              narration: v.narration || '',
              debit: v.debit,
              credit: v.credit,
              balance: `${v.balance} ${v.balanceType}`
            });
          }

          data = {
            company: { name: activeCompany.name, address: activeCompany.address || '' },
            reportMeta: { fromDate, toDate },
            party: {
              name: ledger.name,
              address: '',
              gstin: (ledger as any).gstin || '',
              openingBalance: { amount: openingBalance, type: openingType }
            },
            transactions,
            totals: {
              debit: reportVouchers.reduce((acc, v) => acc + (parseFloat(v.debit) || 0), 0).toFixed(2),
              credit: reportVouchers.reduce((acc, v) => acc + (parseFloat(v.credit) || 0), 0).toFixed(2),
              balance: `${closingBalance} ${closingType}`
            },
            footer: { notes: ['This is a computer generated statement.'] }
          };
        }
      } else if (activeMenuLevel === 'stockitemvouchers' && selectedEntityId) {
        type = 'stock';
        const stockItem = await db.stockItems.get(selectedEntityId);
        const stockGroup = stockItem?.stockGroupId ? await db.stockGroups.get(stockItem.stockGroupId) : null;
        const unit = stockItem ? await db.units.get(stockItem.unitId) : null;
        const inventory = await db.inventoryEntries.where('stockItemId').equals(selectedEntityId).toArray();

        // Pre-load voucherTypes for this company to correctly determine In/Out for custom types
        const voucherTypes = await db.voucherTypes.where('companyId').equals(activeCompany.id!).toArray();
        const vtMap = new Map(voucherTypes.map(vt => [vt.name, vt]));

        const transactions: any[] = [];
        let runningQty = stockItem?.openingBalance || 0;

        transactions.push({
          date: fromDate,
          particulars: 'Opening Balance',
          isOpening: true,
          balance: runningQty,
          value: stockItem?.openingValue || 0
        });

        const invWithVouchers: { inv: any; voucher: any }[] = [];
        for (const inv of inventory) {
          const voucher = await db.vouchers.get(inv.voucherId);
          if (!voucher || voucher.date < fromDate || voucher.date > toDate) continue;
          invWithVouchers.push({ inv, voucher });
        }
        invWithVouchers.sort((a, b) => a.voucher.date.localeCompare(b.voucher.date));

        for (const { inv, voucher } of invWithVouchers) {
          const vt = vtMap.get(voucher.type);
          // stockDirection 'In' → inward; 'Out' → outward
          // Fallback: Purchase/Receipt = inward
          let isInward: boolean;
          if (vt?.stockDirection === 'In') isInward = true;
          else if (vt?.stockDirection === 'Out') isInward = false;
          else isInward = ['Purchase', 'Receipt'].includes(voucher.type);

          if (isInward) runningQty += inv.quantity;
          else runningQty -= inv.quantity;

          transactions.push({
            date: voucher.date,
            particulars: voucher.type,
            vchNo: voucher.voucherNumber,
            inward: isInward ? inv.quantity : '',
            outward: !isInward ? inv.quantity : '',
            balance: runningQty,
            rate: inv.rate,
            value: inv.amount
          });
        }

        data = {
          company: {
            name: activeCompany.name,
            address: activeCompany.address || ''
          },
          item: {
            name: stockItem?.name || '',
            group: stockGroup?.name || 'Primary'
          },
          reportMeta: {
            dateRange: `${fromDate} to ${toDate}`,
            baseUnit: unit?.symbol || 'Nos'
          },
          transactions,
          totals: {
            inward: transactions.reduce((acc, curr) => acc + (parseFloat(curr.inward) || 0), 0),
            outward: transactions.reduce((acc, curr) => acc + (parseFloat(curr.outward) || 0), 0),
            balance: runningQty,
            value: transactions.reduce((acc, curr) => acc + (parseFloat(curr.value) || 0), 0)
          },
          footer: {
            disclaimer: 'Stock values are based on actual transactions.',
            pageInfo: 'Page 1 of 1'
          }
        };
      } else if (activeMenuLevel === 'stocksummary') {
        type = 'stocksummary';
        const ss = await AccountingEngine.generateStockSummary(activeCompany.id!, toDate);
        data = {
          company: { name: activeCompany.name, address: activeCompany.address || '' },
          reportMeta: { toDate },
          items: ss.items,
          totals: {
            quantity: '', // generateStockSummary does not provide global quantity due to mismatched units
            value: ss.totalValue.toFixed(2)
          }
        };
      } else if (activeMenuLevel.startsWith('register_summary_')) {
        type = 'registersummary';
        const vType = activeMenuLevel.split('_')[2];
        const vName = vType.charAt(0).toUpperCase() + vType.slice(1);
        const rs = await AccountingEngine.generateRegisterSummary(activeCompany.id!, vName, fromDate, toDate);
        data = {
          company: { name: activeCompany.name, address: activeCompany.address || '' },
          reportMeta: { voucherType: vName, fromDate, toDate },
          months: rs.map(m => ({
             month: m.name,
             voucherCount: m.count,
             totalDebit: 0,
             totalCredit: 0
          })),
          totals: {
            totalVouchers: rs.reduce((acc, curr) => acc + curr.count, 0),
            totalDebit: '0.00',
            totalCredit: '0.00'
          }
        };
      } else if (activeMenuLevel.startsWith('voucher_register_')) {
        type = 'voucherregister';
        const vType = activeMenuLevel.split('_')[2];
        const vName = vType.charAt(0).toUpperCase() + vType.slice(1);
        const vr = await AccountingEngine.generateVoucherRegister(activeCompany.id!, vName, fromDate.substring(0, 7));
        data = {
          company: { name: activeCompany.name, address: activeCompany.address || '' },
          reportMeta: { voucherType: vName, period: fromDate.substring(0, 7) },
          vouchers: vr,
          totals: {
            totalDebit: vr.reduce((acc, curr) => acc + (parseFloat(curr.debit) || 0), 0).toFixed(2),
            totalCredit: vr.reduce((acc, curr) => acc + (parseFloat(curr.credit) || 0), 0).toFixed(2)
          }
        };
      } else if (activeMenuLevel === 'binbox') {
        type = 'daybook';
        const binboxData = useStore.getState().binboxData || [];
        
        const voucherList = binboxData.map((v: any) => ({
          date: v.date,
          particulars: v.particulars,
          vchType: v.vchType,
          vchNo: v.vchNo,
          refNo: v.refNo,
          narration: v.narration || '',
          deletedAt: v.deletedAt || '',
          debit: v.amount > 0 ? v.amount.toFixed(2) : '',
          credit: ''
        }));

        data = {
          company: { name: activeCompany.name, address: activeCompany.address || '' },
          reportMeta: { fromDate, toDate, title: 'Bin Box (Deleted Vouchers)' },
          vouchers: voucherList,
          totals: { 
            debit: binboxData.reduce((acc: number, curr: any) => acc + (curr.amount || 0), 0).toFixed(2), 
            credit: '0.00' 
          }
        };
      }

      if (data && type) {
        if (exportRequested === 'excel') {
           exportToExcel(type, data);
           setExportRequested(null);
        } else if (exportRequested === 'pdf') {
           exportToPdf(type, data);
           setExportRequested(null);
        } else if (printRequested) {
           // setPrintRequested FALSE pehle karo — warna effect re-fires jab
           // print popup se focus wapas app pe aata hai (printRequested still true
           // rehta tha → gatherData firse run → doosra print window khulta tha).
           setPrintRequested(false);
           setPrintData(data);
           setReportType(type);
        }
      } else {
        alert('No printable/exportable view available for this screen.');
        setPrintRequested(false);
        setExportRequested(null);
      }
    } catch (err: any) {
      console.error('PrintManager gatherData error:', err);
      alert(`Print/Export failed: ${err?.message || 'Unknown error'}`);
      setPrintRequested(false);
      setExportRequested(null);
      isGatheringRef.current = false; // FIX: ensure lock is always released
    }
  };

  if (!printData) return null;

  const zoomStyle: React.CSSProperties = {
    transform: `scale(${zoom / 100})`,
    transformOrigin: 'top center',
    width: '210mm',
    transition: 'transform 0.15s ease',
  };

  return (
    <div
      className="print-container fixed inset-0 z-[9999] overflow-hidden"
      style={{ background: '#cbd5e1', display: 'flex', flexDirection: 'column' }}
    >
      {/* ── Toolbar (screen only) ── */}
      <div
        style={{
          flexShrink: 0,
          background: '#0f766e',
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          gap: '0',
          height: '46px',
          boxShadow: '0 2px 10px rgba(0,0,0,0.25)',
          fontFamily: "'Segoe UI', Arial, sans-serif",
          fontSize: '13px',
          padding: '0 12px',
          justifyContent: 'space-between',
        }}
      >
        {/* LEFT: branding + action buttons */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 0 }}>
          {/* Icon + label */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 6 2 18 2 18 9"/>
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
              <rect x="6" y="14" width="12" height="8"/>
            </svg>
            <span style={{ fontWeight: 700, fontSize: '13px' }}>Print Preview</span>
            <span style={{ fontSize: '11px', color: '#99f6e4', display: 'none' }} className="sm-show">— Review before printing</span>
          </div>

          <div style={{ width: '1px', height: '20px', background: 'rgba(255,255,255,0.2)', flexShrink: 0 }} />

          {/* Print button */}
          <button
            onClick={() => popupPrint(printContentRef.current)}
            style={{
              background: '#fff',
              color: '#0f766e',
              border: 'none',
              borderRadius: '5px',
              padding: '5px 14px',
              fontWeight: 700,
              fontSize: '13px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              flexShrink: 0,
              boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
            }}
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 6 2 18 2 18 9"/>
              <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2"/>
              <rect x="6" y="14" width="12" height="8"/>
            </svg>
            Print
          </button>

          {/* WhatsApp Share button */}
          <button
            title="Share via WhatsApp (W)"
            onClick={() => {
              const d = printData as any;
              let text = '';
              if (d?.meta) {
                const m = d.meta;
                text = `*${d.company?.name || ''}*\n${m.voucherType || ''} No: ${m.referenceNumber || m.voucherNumber || ''}\nDate: ${m.date || ''}\nParty: ${m.party || ''}\nAmount: ₹${m.totalAmount || m.grandTotal || ''}`;
              } else if (d?.company) {
                text = `Invoice from ${d.company.name || ''}\nDate: ${d.date || ''}`;
              } else {
                text = `Document from your accounting software`;
              }
              if (navigator.share) {
                navigator.share({ title: 'Invoice', text }).catch(() => {
                  window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
                });
              } else {
                window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
              }
            }}
            style={{
              background: '#25D366',
              color: '#fff',
              border: 'none',
              borderRadius: '5px',
              padding: '5px 12px',
              fontWeight: 700,
              fontSize: '13px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              flexShrink: 0,
              boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
            }}
          >
            <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
            Share
          </button>

          {/* Close button */}
          <button
            onClick={() => { setPrintRequested(false); setPrintData(null); setReportType(null); setZoom(100); }}
            style={{
              background: 'rgba(255,255,255,0.12)',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.28)',
              borderRadius: '5px',
              padding: '5px 12px',
              fontWeight: 600,
              fontSize: '13px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              flexShrink: 0,
            }}
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
            Close
          </button>
        </div>

        {/* CENTER: zoom controls */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
          {/* Zoom out */}
          <button
            onClick={() => changeZoom(-10)}
            title="Zoom Out"
            style={{
              background: 'rgba(255,255,255,0.12)',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: '4px',
              width: '28px', height: '28px',
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '16px', fontWeight: 700, lineHeight: 1,
            }}
          >−</button>

          {/* Zoom display */}
          <div style={{
            background: 'rgba(0,0,0,0.2)',
            borderRadius: '4px',
            padding: '3px 10px',
            fontSize: '12px',
            fontWeight: 700,
            minWidth: '48px',
            textAlign: 'center',
            color: '#e2f8f5',
            letterSpacing: '0.5px',
          }}>
            {zoom}%
          </div>

          {/* Zoom in */}
          <button
            onClick={() => changeZoom(10)}
            title="Zoom In"
            style={{
              background: 'rgba(255,255,255,0.12)',
              color: '#fff',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: '4px',
              width: '28px', height: '28px',
              cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '16px', fontWeight: 700, lineHeight: 1,
            }}
          >+</button>

          {/* Fit button */}
          <button
            onClick={autoFitZoom}
            title="Fit to screen"
            style={{
              background: 'rgba(255,255,255,0.12)',
              color: '#e2f8f5',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: '4px',
              padding: '3px 8px',
              fontSize: '11px',
              fontWeight: 600,
              cursor: 'pointer',
              whiteSpace: 'nowrap',
            }}
          >Fit</button>
        </div>

        {/* RIGHT: page info */}
        <div style={{ minWidth: '80px', textAlign: 'right', fontSize: '11px', color: '#99f6e4', flexShrink: 0 }}>
          A4 Preview
        </div>
      </div>

      {/* ── Scrollable content area ── */}
      <div
        ref={contentRef}

        style={{
          flexGrow: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: '24px 24px',
          boxSizing: 'border-box',
        }}
      >
        {/* Scaled page wrapper — height compensated so scroll container is accurate */}
        <div style={{
          width: '210mm',
          display: 'flex',
          justifyContent: 'center',
          marginBottom: `${zoom < 100 ? -((1 - zoom/100) * 297 * 3.7795) : 0}px`, // mm→px offset correction
        }}>
          <div style={zoomStyle}>
            <div ref={printContentRef}>
              {reportType === 'invoice' && <Invoice data={printData} />}
              {reportType === 'pharmacy_invoice' && <PharmacyInvoice data={printData} />}
              {reportType === 'voucher' && <VoucherPrint data={printData} />}
              {reportType === 'ledger' && <LedgerReport data={printData} />}
              {reportType === 'stock' && <StockMovementReport data={printData} />}
              {reportType === 'daybook' && <DayBookPrint data={printData} />}
              {reportType === 'trialbalance' && <TrialBalancePrint data={printData} />}
              {reportType === 'balancesheet' && <BalanceSheetPrint data={printData} />}
              {reportType === 'profitloss' && <ProfitLossPrint data={printData} />}
              {reportType === 'stocksummary' && <StockSummaryPrint data={printData} />}
              {reportType === 'registersummary' && <RegisterSummaryPrint data={printData} />}
              {reportType === 'voucherregister' && <VoucherRegisterPrint data={printData} />}
              {reportType === 'quotation' && <QuotationPrint data={printData} />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};